"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { cn } from "@/src/lib/utils";
import { NAVIGATION_START_EVENT } from "@/src/shared/utils/navigationHistory";
import { detailsLoadingService } from "@/src/shared/services/detailsLoading";

/** A quick navigation lands before this, so it never flashes the skeleton. */
const SHOW_DELAY_MS = 120;
/** A navigation that never arrives (an error, offline) stops showing as loading. */
const GIVE_UP_MS = 15_000;

/** A loading placeholder in the page's grey. */
const Bone: React.FC<{ className: string }> = ({ className }) => (
  <div className={cn("animate-pulse rounded-md bg-[#EBEBEB]", className)} />
);

/** The desktop details page while it loads: title, the photo grid, the info and the booking card. */
const DetailsSkeleton: React.FC = () => (
  <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
    <div className="flex items-start justify-between gap-6">
      <Bone className="h-8 w-[min(560px,60%)]" />
      <div className="flex gap-4">
        <Bone className="h-6 w-16" />
        <Bone className="h-6 w-16" />
      </div>
    </div>
    <div className="mt-6 flex h-[418px] gap-2 overflow-hidden rounded-xl">
      <Bone className="h-full w-1/2 rounded-none" />
      <div className="grid h-full w-1/2 grid-cols-2 grid-rows-2 gap-2">
        {[0, 1, 2, 3].map((tile) => (
          <Bone key={tile} className="h-full w-full rounded-none" />
        ))}
      </div>
    </div>
    <div className="mt-8 grid grid-cols-12 gap-8 lg:gap-16">
      <div className="col-span-7 space-y-3">
        <Bone className="h-6 w-2/3" />
        <Bone className="h-4 w-1/2" />
        <Bone className="h-4 w-40" />
        <div className="!mt-8 flex items-center gap-4 border-t border-[#EBEBEB] pt-8">
          <Bone className="h-14 w-14 rounded-full" />
          <div className="flex-1 space-y-2">
            <Bone className="h-4 w-48" />
            <Bone className="h-3 w-64" />
          </div>
        </div>
      </div>
      <div className="col-span-5">
        <div className="space-y-4 rounded-2xl border border-[#EBEBEB] p-6">
          <Bone className="h-7 w-40" />
          <Bone className="h-14 w-full rounded-lg" />
          <Bone className="h-14 w-full rounded-lg" />
          <Bone className="h-12 w-full rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Desktop: a link into a listing's or hotel's page shows the page's shape at once, under
 * the site header, until the page arrives. The server still renders the page (its 404s
 * and redirects stay real); this only covers the wait. Mounted once, in `AppShell`.
 */
const DetailsLoading: React.FC = () => {
  const pathname = usePathname() ?? "";
  const [target, setTarget] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [top, setTop] = useState(0);
  const timers = useRef<number[]>([]);

  const clear = () => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
    setTarget(null);
    setVisible(false);
  };

  useEffect(() => {
    const onStart = (event: Event) => {
      const to = (event as CustomEvent<string>).detail;
      if (
        !detailsLoadingService.shouldShow(
          window.location.pathname,
          to,
          window.innerWidth,
        )
      )
        return;
      clear();
      // Under the site header, wherever the page was scrolled.
      const header = document.querySelector("header");
      setTop(Math.max(0, header?.getBoundingClientRect().bottom ?? 0));
      setTarget(to);
      timers.current.push(
        window.setTimeout(() => setVisible(true), SHOW_DELAY_MS),
        window.setTimeout(clear, GIVE_UP_MS),
      );
    };
    window.addEventListener(NAVIGATION_START_EVENT, onStart);
    // A back or forward is not the navigation it was waiting for.
    window.addEventListener("popstate", clear);
    return () => {
      window.removeEventListener(NAVIGATION_START_EVENT, onStart);
      window.removeEventListener("popstate", clear);
      timers.current.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  // The page arrived (or the navigation went elsewhere): step aside.
  useEffect(() => {
    if (target) clear();
  }, [pathname]);

  if (!target || !visible) return null;
  return createPortal(
    <div
      aria-busy="true"
      aria-label="Loading"
      className="fixed inset-x-0 bottom-0 z-40 overflow-hidden bg-white"
      style={{ top }}
    >
      <DetailsSkeleton />
    </div>,
    document.body,
  );
};

export default DetailsLoading;
